/**
 * Subnet rules for the Discover step (web multi-protocol brief section 5):
 * the "network to scan" picker built from the gateway's birth `networks`, the
 * custom-range check, and the manual-address warning.
 *
 * The range check mirrors `protocol.ValidateScanTarget`
 * (platform-core/pkg/protocol/scan.go): IPv4 only, inside 10/8, 172.16/12,
 * 192.168/16 or 127/8, and no wider than a /20. The server enforces it again
 * (400 `invalid_scan_target`); checking here saves a 25 s round trip on a typo.
 */

import type { GatewayNetwork } from "@/lib/api/types";

/** The widest range a scan may cover (a /20 is 4096 addresses). */
export const MIN_SCAN_PREFIX = 20;

/** The gateway's sweep budget, about 20 s: past it the gateway returns what it found, marked partial. */
export const SWEEP_BUDGET_S = 20;

/** 127.0.0.0/8: the gateway itself (an app on its own host, such as a results feed). */
const LOOPBACK_NET = ipv4ToInt("127.0.0.0")!;

const ALLOWED: Array<[number, number]> = [
  [ipv4ToInt("10.0.0.0")!, 8],
  [ipv4ToInt("172.16.0.0")!, 12],
  [ipv4ToInt("192.168.0.0")!, 16],
  [LOOPBACK_NET, 8],
];

function isLoopback(ip: number): boolean {
  return networkOf(ip, 8) === LOOPBACK_NET;
}

/** A dotted quad as an unsigned 32-bit number, or null when it is not one (no leading zeros, no spaces). */
export function ipv4ToInt(s: string): number | null {
  const parts = s.split(".");
  if (parts.length !== 4) return null;
  let v = 0;
  for (const p of parts) {
    if (!/^\d{1,3}$/.test(p) || (p.length > 1 && p.startsWith("0"))) return null;
    const n = Number(p);
    if (n > 255) return null;
    v = v * 256 + n;
  }
  return v;
}

export function intToIpv4(v: number): string {
  return [24, 16, 8, 0].map((sh) => Math.floor(v / 2 ** sh) % 256).join(".");
}

/** The network address of `ip` under `prefix` (host bits cleared). */
function networkOf(ip: number, prefix: number): number {
  const size = 2 ** (32 - prefix);
  return Math.floor(ip / size) * size;
}

export interface ParsedCidr {
  /** The network address. */
  network: number;
  prefix: number;
  /** Canonical text: "10.2.10.0/24", or a bare address for a /32. */
  text: string;
}

/** "10.2.10.40/24" -> {network 10.2.10.0, prefix 24}; a bare address is a /32. Null when not IPv4. */
export function parseCidr(s: string): ParsedCidr | null {
  const t = s.trim();
  const [addr, pre, ...rest] = t.split("/");
  if (rest.length > 0) return null;
  const ip = ipv4ToInt(addr);
  if (ip === null) return null;
  let prefix = 32;
  if (pre !== undefined) {
    if (!/^\d{1,2}$/.test(pre)) return null;
    prefix = Number(pre);
    if (prefix > 32) return null;
  }
  const network = networkOf(ip, prefix);
  return { network, prefix, text: prefix === 32 ? intToIpv4(network) : `${intToIpv4(network)}/${prefix}` };
}

/** Addresses a scan of this prefix probes: every usable host (a /32 is one, a /31 two). */
export function hostCount(prefix: number): number {
  if (prefix >= 31) return 2 ** (32 - prefix);
  return 2 ** (32 - prefix) - 2;
}

function allowed(network: number, prefix: number): boolean {
  return ALLOWED.some(([base, bits]) => prefix >= bits && networkOf(network, bits) === base);
}

export type ScanTargetCheck =
  | {
      ok: true;
      /** What to send: the canonical range, or "" for the gateway's own network. */
      cidr: string;
      /** Addresses probed; null for the gateway's own network (the gateway decides, a /24). */
      hosts: number | null;
      /** A warning that does not block the scan (a range wider than a /24 may come back partial). */
      warning?: string;
    }
  | { ok: false; error: string };

/**
 * A typed scan range, checked the way the server checks it. Empty = the
 * gateway's own network (its plant-LAN /24), always allowed.
 */
export function checkScanTarget(text: string): ScanTargetCheck {
  const t = text.trim();
  if (t === "") return { ok: true, cidr: "", hosts: null };
  const p = parseCidr(t);
  if (!p) {
    return {
      ok: false,
      error: `"${t}" is not an IPv4 address or range. Use a form like 192.168.1.0/24 or 192.168.1.50.`,
    };
  }
  if (p.prefix < MIN_SCAN_PREFIX) {
    return {
      ok: false,
      error: `${p.text} is too wide: ${hostCount(p.prefix).toLocaleString("en-US")} addresses. Use a /${MIN_SCAN_PREFIX} or narrower.`,
    };
  }
  if (!allowed(p.network, p.prefix)) {
    return {
      ok: false,
      error: `${p.text} is not a private or loopback address. Gateways only scan 10.x, 172.16-31.x, 192.168.x and 127.x.`,
    };
  }
  const hosts = hostCount(p.prefix);
  return {
    ok: true,
    cidr: p.text,
    hosts,
    warning: p.prefix < 24 ? wideRangeWarning(hosts) : undefined,
  };
}

function wideRangeWarning(hosts: number): string {
  return `${hosts.toLocaleString("en-US")} addresses is more than a /24. The gateway stops after about ${SWEEP_BUDGET_S} seconds and returns what it found so far, so the list may be incomplete. Narrow the range if you can.`;
}

/** "254 addresses, up to about 20 s" for a range the picker or the custom field resolves to. */
export function scanEstimate(hosts: number | null): string {
  if (hosts === null) return `the gateway's own network, up to about ${SWEEP_BUDGET_S} s`;
  const n = `${hosts.toLocaleString("en-US")} address${hosts === 1 ? "" : "es"}`;
  return hosts > 254 ? `${n}, stops at about ${SWEEP_BUDGET_S} s (may be partial)` : `${n}, up to about ${SWEEP_BUDGET_S} s`;
}

/** One entry of the "network to scan" picker. */
export interface NetworkOption {
  /** Stable id: "<iface>|<interface address>". */
  id: string;
  iface: string;
  /** What is scanned: the interface's network ("10.2.10.0/24"), narrowed to a /24 when it is wider than a /20. */
  cidr: string;
  /** The interface's own address with its prefix, as reported ("10.2.10.40/24"). */
  address: string;
  /** "eth0 · 10.2.10.0/24 · internet" / "wlan0 · 10.2.10.0/24 · Wi-Fi Burrow_2_4G". */
  label: string;
  /** Carries the gateway's default route (the internet), not the plant LAN. */
  internet: boolean;
  up: boolean;
  hosts: number;
  /** Set when the interface's network was wider than a scan may be, so only the /24 around the gateway is offered. */
  narrowed?: string;
}

/**
 * The picker's options: one per IPv4 address in the birth's `networks`, down
 * interfaces included (marked). Loopback and non-IPv4 addresses are left out
 * (the custom range still reaches 127.x). A network wider than a /20 cannot be
 * scanned, so the /24 around the gateway's own address is offered instead.
 */
export function networkOptions(networks: GatewayNetwork[] | null | undefined): NetworkOption[] {
  const out: NetworkOption[] = [];
  for (const n of networks ?? []) {
    for (const address of n.cidrs) {
      const ipText = address.split("/")[0];
      const ip = ipv4ToInt(ipText);
      const p = parseCidr(address);
      if (ip === null || !p || !address.includes("/")) continue;
      if (isLoopback(ip)) continue;
      let prefix = p.prefix;
      let narrowed: string | undefined;
      if (prefix < MIN_SCAN_PREFIX) {
        narrowed = `the network is a /${prefix}; scanning the /24 around the gateway`;
        prefix = 24;
      }
      const cidr = `${intToIpv4(networkOf(ip, prefix))}/${prefix}`;
      const tags = [n.default_route ? "internet" : null, n.kind === "wifi" ? `Wi-Fi${n.ssid ? ` ${n.ssid}` : ""}` : null, n.up ? null : "down"];
      out.push({
        id: `${n.iface}|${address}`,
        iface: n.iface,
        cidr,
        address,
        label: [n.iface, cidr, ...tags.filter(Boolean)].join(" · "),
        internet: n.default_route === true,
        up: n.up,
        hosts: hostCount(prefix),
        narrowed,
      });
    }
  }
  return out;
}

/**
 * The default pick: the first up network that does not carry the internet
 * (the plant LAN), else the first up one (the internet one), else none.
 */
export function defaultNetworkId(options: NetworkOption[]): string | null {
  return (options.find((o) => o.up && !o.internet) ?? options.find((o) => o.up))?.id ?? null;
}

/**
 * The ranges to scan for a set of picked options, in picker order, each
 * network once (two interfaces on one subnet, say eth0 and wlan0, are one scan).
 */
export function scanTargets(options: NetworkOption[], picked: ReadonlySet<string>): NetworkOption[] {
  const seen = new Set<string>();
  return options.filter((o) => {
    if (!picked.has(o.id) || seen.has(o.cidr)) return false;
    seen.add(o.cidr);
    return true;
  });
}

/** True when `ip` is inside one of the ranges (interface addresses with their prefix, or plain networks). */
export function ipInAnyCidr(ip: string, cidrs: string[]): boolean {
  const v = ipv4ToInt(ip.trim());
  if (v === null) return false;
  return cidrs.some((c) => {
    const p = parseCidr(c);
    return !!p && networkOf(v, p.prefix) === p.network;
  });
}

/**
 * The manual-add warning: a typed address outside every network the gateway
 * reported. Null when it is inside one, when the gateway reported none (older
 * firmware: nothing to compare with), when it is not an IPv4 address (a
 * hostname), or when it is loopback (an app on the gateway itself, which the
 * gateway never lists as a network). Never blocks: routed plant networks exist.
 */
export function manualAddressWarning(ip: string, networks: GatewayNetwork[] | null | undefined): string | null {
  const all = (networks ?? []).flatMap((n) => n.cidrs);
  const v = ipv4ToInt(ip.trim());
  if (all.length === 0 || v === null || isLoopback(v)) return null;
  if (ipInAnyCidr(ip, all)) return null;
  return `${ip.trim()} is not on any of the gateway's networks, so it may be unreachable. Add it anyway if the plant network routes to it.`;
}

/** The short name of a scan target: "wlan0 · 10.2.10.0/24" (the picker label without its tags). */
export function shortNetworkLabel(o: Pick<NetworkOption, "iface" | "cidr">): string {
  return `${o.iface} · ${o.cidr}`;
}

/**
 * The collapsed "Scan options" line: where, what and (Modbus) which port,
 * e.g. "wlan0 · 10.2.10.0/24 · all protocols" or
 * "10.2.10.0/24 + 1 more · Modbus TCP, Siemens S7 · port 5020".
 */
export function scanOptionsSummary(o: {
  /** Short names of the ranges to scan, in order. */
  targets: string[];
  /** Labels of the protocols to probe. */
  protocols: string[];
  /** Every protocol the gateway can probe is ticked. */
  allProtocols: boolean;
  /** A Modbus port other than the default, when set. */
  port?: number | null;
  /** Changed scan ports, one note per driver ("EtherNet/IP port 44819"). */
  ports?: string[];
}): string {
  const where =
    o.targets.length === 0
      ? "no network picked"
      : o.targets.length === 1
        ? o.targets[0]
        : `${o.targets[0]} + ${o.targets.length - 1} more`;
  const what =
    o.protocols.length === 0 ? "no protocol" : o.allProtocols && o.protocols.length > 1 ? "all protocols" : o.protocols.join(", ");
  return [where, what, o.port ? `port ${o.port}` : null, ...(o.ports ?? [])].filter(Boolean).join(" · ");
}

/** A typed Modbus port: blank = the default (null), else 1-65535, else an error. */
export function checkScanPort(text: string): { ok: true; port: number | null } | { ok: false; error: string } {
  const t = text.trim();
  if (t === "") return { ok: true, port: null };
  const n = Number(t);
  if (!Number.isInteger(n) || n < 1 || n > 65535) return { ok: false, error: "A port is a whole number from 1 to 65535." };
  return { ok: true, port: n };
}
